import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';

export type CorporatePermission =
  | 'view_balances'
  | 'view_transactions'
  | 'make_payments'
  | 'approve_payments'
  | 'manage_beneficiaries'
  | 'create_virtual_cards'
  | 'order_physical_cards'
  | 'view_all_cards'
  | 'manage_own_cards'
  | 'set_member_card_limits'
  | 'invite_members'
  | 'manage_members'
  | 'view_settings'
  | 'manage_settings'
  | 'exchange_currency';

export const ALL_PERMISSIONS: { key: CorporatePermission; label: string; category: string }[] = [
  { key: 'view_balances', label: 'View Balances', category: 'Finance' },
  { key: 'view_transactions', label: 'View Transactions', category: 'Finance' },
  { key: 'make_payments', label: 'Make Payments', category: 'Finance' },
  { key: 'approve_payments', label: 'Approve Payments', category: 'Finance' },
  { key: 'exchange_currency', label: 'Exchange Currency', category: 'Finance' },
  { key: 'manage_beneficiaries', label: 'Manage Beneficiaries', category: 'Finance' },
  { key: 'create_virtual_cards', label: 'Create Virtual Cards', category: 'Cards' },
  { key: 'order_physical_cards', label: 'Order Physical Cards', category: 'Cards' },
  { key: 'view_all_cards', label: 'View All Company Cards', category: 'Cards' },
  { key: 'manage_own_cards', label: 'Manage Own Cards', category: 'Cards' },
  { key: 'set_member_card_limits', label: 'Set Member Card Limits', category: 'Cards' },
  { key: 'invite_members', label: 'Invite Members', category: 'Team' },
  { key: 'manage_members', label: 'Manage Members', category: 'Team' },
  { key: 'view_settings', label: 'View Settings', category: 'Settings' },
  { key: 'manage_settings', label: 'Manage Settings', category: 'Settings' },
];

export type CorporateRole = 'admin' | 'finance_manager' | 'card_manager' | 'payments_officer' | 'viewer' | 'custom';

export const ROLE_PRESETS: { key: CorporateRole; label: string; description: string; permissions: CorporatePermission[] }[] = [
  {
    key: 'admin',
    label: 'Admin',
    description: 'Full access to all features',
    permissions: ALL_PERMISSIONS.map(p => p.key),
  },
  {
    key: 'finance_manager',
    label: 'Finance Manager',
    description: 'Balances, transactions, payments & FX',
    permissions: ['view_balances', 'view_transactions', 'make_payments', 'approve_payments', 'exchange_currency', 'manage_beneficiaries', 'view_settings'],
  },
  {
    key: 'payments_officer',
    label: 'Payments Officer',
    description: 'Send payments & manage beneficiaries',
    permissions: ['view_balances', 'view_transactions', 'make_payments', 'manage_beneficiaries'],
  },
  {
    key: 'card_manager',
    label: 'Card Manager',
    description: 'Issue & manage company cards',
    permissions: ['create_virtual_cards', 'order_physical_cards', 'view_all_cards', 'manage_own_cards', 'set_member_card_limits', 'view_balances'],
  },
  {
    key: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to balances & transactions',
    permissions: ['view_balances', 'view_transactions', 'view_settings'],
  },
  {
    key: 'custom',
    label: 'Custom',
    description: 'Pick permissions manually',
    permissions: [],
  },
];

interface CorporateMember {
  id: string;
  role: string;
  display_name: string | null;
  job_title: string | null;
  kyc_status?: string;
  sumsub_applicant_id?: string | null;
  kyc_verified_at?: string | null;
}

interface CorporatePermissionContextType {
  isCorporate: boolean;
  member: CorporateMember | null;
  permissions: CorporatePermission[];
  loading: boolean;
  hasPermission: (perm: CorporatePermission) => boolean;
  isOwner: boolean;
  kycStatus: string | null;
  kycRequired: boolean;
  refresh: () => void;
}

const CorporatePermissionContext = createContext<CorporatePermissionContextType>({
  isCorporate: false,
  member: null,
  permissions: [],
  loading: true,
  hasPermission: () => true,
  isOwner: false,
  kycStatus: null,
  kycRequired: false,
  refresh: () => {},
});

export function CorporatePermissionProvider({ children }: { children: ReactNode }) {
  const { profile, user } = useCustomerAuth();
  const [member, setMember] = useState<CorporateMember | null>(null);
  const [permissions, setPermissions] = useState<CorporatePermission[]>([]);
  const [loading, setLoading] = useState(true);

  const isCorporate = profile?.client_type === 'corporate';

  const fetchPermissions = useCallback(async () => {
    if (!isCorporate || !profile?.id || !user) {
      setMember(null);
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('corporate-members', {
        body: { action: 'my_permissions', client_id: profile.id },
      });

      // If we get a 401/Unauthorized, the session is dead – force re-login
      if (error) {
        const msg = typeof error === 'object' && error !== null ? (error as any).message || JSON.stringify(error) : String(error);
        if (msg.includes('Unauthorized') || msg.includes('401')) {
          console.warn('[permissions] session expired, signing out');
          await supabase.auth.signOut();
          return;
        }
        throw error;
      }
      setMember(data?.member || null);
      setPermissions(data?.permissions || []);
    } catch {
      // If no membership found, individual users get all permissions
      setMember(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [isCorporate, profile?.id, user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (perm: CorporatePermission): boolean => {
      // Individual accounts have all permissions
      if (!isCorporate) return true;
      // If no member record (sole user/owner), grant all
      if (!member) return true;
      // Owner has all
      if (member.role === 'owner') return true;
      return permissions.includes(perm);
    },
    [isCorporate, member, permissions]
  );

  const isOwner = member?.role === 'owner' || (!isCorporate);
  const kycStatus = member?.kyc_status || null;
  const kycRequired = isCorporate && !!member && member.role !== 'owner' && member.kyc_status !== 'approved';

  return (
    <CorporatePermissionContext.Provider
      value={{ isCorporate, member, permissions, loading, hasPermission, isOwner, kycStatus, kycRequired, refresh: fetchPermissions }}
    >
      {children}
    </CorporatePermissionContext.Provider>
  );
}

export function useCorporatePermissions() {
  return useContext(CorporatePermissionContext);
}
